import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import translateService from "./translateService";

// Which realm owns the current browser session (shared across tabs).
const REALM_KEY = "validatege.oidc.realm";

// An organization share's corporate login (share-prompt task 17) is one
// tab's only: that tab's session lives in its sessionStorage and outranks the
// shared one there, and every other tab keeps the shared session. Set before
// the redirect to Keycloak, so the callback knows the session is the tab's.
const TAB_REALM_KEY = "validatege.oidc.tab-realm";

const readTabRealm = () => {
  try {
    return sessionStorage.getItem(TAB_REALM_KEY) || null;
  } catch (e) {
    return null;
  }
};

const writeTabRealm = (realm) => {
  try {
    if (realm) sessionStorage.setItem(TAB_REALM_KEY, realm);
    else sessionStorage.removeItem(TAB_REALM_KEY);
  } catch (e) {
    /* storage unavailable: the login falls back to the shared session */
  }
};

// Per-realm settings. Redirect URIs are computed at runtime from the current
// origin: web_URL points at validate.ge even in local builds.
const REALMS = {
  b2c: {
    authorityKey: "oidc_b2c_authority",
    scope: "openid profile email",
    callback: "auth/callback",
  },
  corp: {
    authorityKey: "oidc_corp_authority",
    scope: "openid profile email",
    callback: "auth/callback/hrms",
  },
};

const appBase = () => window.location.origin + globalConfig.base_URL;
const trimSlash = (path) => path.replace(/\/+$/, "");
const isFresh = (user, minSeconds) =>
  !!user && !user.expired && user.expires_in > minSeconds;

// Authorization Code + PKCE against Keycloak. One UserManager per realm; the
// session (access/refresh/id token) lives in localStorage so all tabs share one
// refresh chain (required for refresh-token rotation). Refresh is driven here,
// single-flight per tab and serialized across tabs with the Web Locks API.
class OidcService {
  constructor() {
    this._managers = {};
    this._user = null; // in-memory mirror for synchronous isAuthenticated()
    this._realm = null;
    this._refreshing = null;
    this._listeners = [];
    this.callbackError = null;
    // where the callback page should navigate after a successful code exchange
    this.postLoginTarget = null;
    // Back from Keycloak can restore this page from the back/forward cache
    // without restore(): a tab marker without its user is an abandoned
    // tab-only login, and the tab goes back to the shared session
    if (typeof window !== "undefined") {
      window.addEventListener("pageshow", (e) => {
        const tab = e.persisted && readTabRealm();
        if (!tab) return;
        this.manager(tab, true)
          .getUser()
          .catch(() => null)
          .then((user) => {
            if (user) return;
            writeTabRealm(null);
            this.syncFromStorage();
          });
      });
    }
  }

  // ---- configuration -------------------------------------------------

  settings(realm, tab = false) {
    const r = REALMS[realm];
    if (!r) throw new Error(`Unknown realm "${realm}"`);
    const store = tab ? window.sessionStorage : window.localStorage;
    return {
      authority: globalConfig[r.authorityKey],
      client_id: globalConfig.oidc_client_id,
      redirect_uri: appBase() + r.callback,
      post_logout_redirect_uri: appBase() + "landing",
      response_type: "code", // PKCE S256 is implicit in oidc-client-ts 3.x
      scope: r.scope,
      automaticSilentRenew: false, // refresh is driven by ensureFreshUser()
      monitorSession: false, // check_session iframe needs third-party cookies
      loadUserInfo: false, // identity comes from GET api/private/userInfo
      revokeTokensOnSignout: false,
      userStore: new WebStorageStateStore({ store }),
      stateStore: new WebStorageStateStore({ store }),
      // no silent_redirect_uri on purpose: signinSilent() may only use the
      // refresh token, never a hidden iframe
    };
  }

  // the realm's manager for this tab: the tab's own session when the tab
  // holds one of that realm, else the shared session's
  manager(realm, tab = readTabRealm() === realm) {
    const key = tab ? `${realm}:tab` : realm;
    if (!this._managers[key]) {
      const m = new UserManager(this.settings(realm, tab));
      m.events.addUserLoaded((user) => {
        if (this.activeRealm() === realm) this._setUser(user);
      });
      m.events.addUserUnloaded(() => {
        if (this.activeRealm() === realm) this._setUser(null);
      });
      this._managers[key] = m;
    }
    return this._managers[key];
  }

  // this tab holds a session of its own (an organization share's login)
  isTabScoped() {
    return !!readTabRealm();
  }

  activeRealm() {
    const tab = readTabRealm();
    if (tab) return tab;
    if (this._realm) return this._realm;
    try {
      return localStorage.getItem(REALM_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  currentUser() {
    return this._user;
  }

  isCallbackPath(pathname) {
    return !!this._callbackRealm(pathname);
  }

  // fn(user, previousUser) on every change of the in-memory mirror
  onUserChange(fn) {
    this._listeners.push(fn);
    return {
      unsubscribe: () => {
        const i = this._listeners.indexOf(fn);
        if (i !== -1) this._listeners.splice(i, 1);
      },
    };
  }

  _callbackRealm(pathname) {
    const path = trimSlash(pathname || "");
    for (const realm in REALMS) {
      if (path === trimSlash(globalConfig.base_URL + REALMS[realm].callback))
        return realm;
    }
    return null;
  }

  _setRealm(realm) {
    // a tab's own session never touches the shared realm
    if (readTabRealm()) {
      writeTabRealm(realm);
      return;
    }
    this._realm = realm;
    try {
      if (realm) localStorage.setItem(REALM_KEY, realm);
      else localStorage.removeItem(REALM_KEY);
    } catch (e) {
      /* storage unavailable: session stays in memory only */
    }
  }

  _setUser(user) {
    const previous = this._user;
    this._user = user || null;
    if (previous === this._user) return;
    for (const fn of this._listeners) fn(this._user, previous);
  }

  _safeReturnTo(value) {
    if (typeof value !== "string") return null;
    if (!value.startsWith("/") || value.startsWith("//")) return null;
    return value;
  }

  // ---- bootstrap -----------------------------------------------------

  // Called once before the app renders. On a callback route it finishes the
  // code exchange and rewrites the URL to the requested page; otherwise it
  // loads the stored session. Never starts a login by itself.
  async restore() {
    const realm = this._callbackRealm(window.location.pathname);
    if (realm) return this._finishCallback(realm);

    // a tab's own session; without its user (the login was abandoned) the
    // tab goes back to the shared session
    const tab = readTabRealm();
    if (tab) {
      const user = await this.manager(tab, true).getUser().catch(() => null);
      if (user) {
        this._setUser(user);
        return user;
      }
      writeTabRealm(null);
    }

    const active = this.activeRealm();
    if (!active) return null;
    const user = await this.manager(active).getUser().catch(() => null);
    if (!user) {
      this._setRealm(null);
      return null;
    }
    this._realm = active;
    this._setUser(user);
    return user;
  }

  async _finishCallback(realm) {
    // the tab's own login (set before the redirect): the shared session and
    // every other tab stay as they are
    const tab = readTabRealm();
    if (tab && tab !== realm) writeTabRealm(null);
    if (tab === realm) return this._finishTabCallback(realm);

    const previous = this.activeRealm();
    this._setRealm(realm);
    try {
      const user = await this.manager(realm).signinCallback(window.location.href);
      if (!user) throw new Error("No user returned from the sign-in callback");
      this._setUser(user);
      if (previous && previous !== realm) {
        await this.manager(previous).removeUser().catch(() => {});
      }
      // The router owns navigation: the callback page performs the replace
      // (a native history.replaceState here would leave react-router on the
      // callback route).
      this.postLoginTarget =
        this._safeReturnTo(user.state && user.state.returnTo) ||
        globalConfig.base_URL + "dashboard";
      return user;
    } catch (err) {
      this.callbackError = err;
      this._setRealm(previous || null);
      if (previous) {
        const user = await this.manager(previous).getUser().catch(() => null);
        this._setUser(user);
      }
      return null;
    }
  }

  async _finishTabCallback(realm) {
    try {
      const user = await this.manager(realm, true).signinCallback(window.location.href);
      if (!user) throw new Error("No user returned from the sign-in callback");
      this._setUser(user);
      this.postLoginTarget =
        this._safeReturnTo(user.state && user.state.returnTo) ||
        globalConfig.base_URL + "dashboard";
      return user;
    } catch (err) {
      this.callbackError = err;
      writeTabRealm(null);
      const shared = this.activeRealm();
      if (shared) this._setUser(await this.manager(shared, false).getUser().catch(() => null));
      return null;
    }
  }

  // Another tab changed the session: reload the mirror from localStorage.
  async syncFromStorage() {
    let active = null;
    try {
      active = localStorage.getItem(REALM_KEY) || null;
    } catch (e) {
      /* ignore */
    }
    this._realm = active;
    if (!active) {
      this._setUser(null);
      return null;
    }
    const user = await this.manager(active).getUser().catch(() => null);
    this._setUser(user);
    return user;
  }

  // ---- tokens --------------------------------------------------------

  // Returns a user whose access token is valid for at least minSeconds,
  // refreshing it if necessary; null when there is no usable session.
  async ensureFreshUser(minSeconds = 30) {
    const realm = this.activeRealm();
    if (!realm) return null;
    const user = await this.manager(realm).getUser().catch(() => null);
    if (!user) {
      this._setUser(null);
      return null;
    }
    if (isFresh(user, minSeconds)) {
      this._setUser(user);
      return user;
    }
    if (!user.refresh_token) {
      this._setUser(user);
      return user.expired ? null : user;
    }
    return this.refreshSingleFlight();
  }

  // refresh_token grant, once per tab at a time, serialized across tabs.
  // force: refresh even a fresh token (503 ORGANIZATIONS_UNAVAILABLE: the
  // backend's Keycloak call needs a live SSO session, which only a refresh
  // proves); another tab's refresh meanwhile still counts
  refreshSingleFlight(force = false) {
    if (this._refreshing) return this._refreshing;
    const realm = this.activeRealm();
    if (!realm) return Promise.resolve(null);
    const m = this.manager(realm);
    const before = this._user && this._user.access_token;
    const done = (user) => isFresh(user, 30) && (!force || user.access_token !== before);

    const run = async () => {
      const current = await m.getUser().catch(() => null);
      if (done(current)) return current; // another tab already refreshed
      try {
        return await m.signinSilent();
      } catch (err) {
        const again = await m.getUser().catch(() => null);
        if (done(again)) return again; // rotated by another tab meanwhile
        throw err;
      }
    };

    // a tab's own session has its own refresh chain: nothing to serialize
    const withLock =
      typeof navigator !== "undefined" && navigator.locks && !this.isTabScoped()
        ? navigator.locks.request("validatege-oidc-refresh", run)
        : run();

    this._refreshing = withLock
      .then((user) => {
        this._setUser(user);
        return user;
      })
      .finally(() => {
        this._refreshing = null;
      });
    return this._refreshing;
  }

  // ---- flows ---------------------------------------------------------

  // realm: "b2c" | "corp"; register -> prompt=create; idpHint -> kc_idp_hint;
  // loginHint -> login_hint; prompt (e.g. "login": another account);
  // tabOnly: the session is this tab's alone (an organization share)
  login({ realm = "b2c", returnTo, idpHint, register, loginHint, prompt, tabOnly } = {}) {
    const args = {
      state: { realm, returnTo: returnTo || null },
      ui_locales: translateService.key,
    };
    if (register) args.prompt = "create";
    if (prompt) args.prompt = prompt;
    if (loginHint) args.login_hint = loginHint;
    if (idpHint) args.extraQueryParams = { kc_idp_hint: idpHint };
    writeTabRealm(tabOnly ? realm : null);
    return this.manager(realm, !!tabOnly).signinRedirect(args);
  }

  // the signed-in person's e-mail, as the token states it
  email() {
    const profile = this._user && this._user.profile;
    return (profile && profile.email) || null;
  }

  // RP-initiated logout: ends the Keycloak session and lands on /landing, or
  // on `returnTo` (a path under the app, e.g. "l": an organization share's
  // "sign in as another account" - Keycloak refuses a second user while its
  // SSO session holds one)
  async logout({ returnTo } = {}) {
    const realm = this.activeRealm();
    const m = realm ? this.manager(realm) : null;
    const target = appBase() + (returnTo || "landing");
    this._setRealm(null);
    this._setUser(null);
    if (!m) {
      window.location.assign(target);
      return;
    }
    try {
      await m.signoutRedirect({ post_logout_redirect_uri: target });
    } catch (err) {
      await m.removeUser().catch(() => {});
      window.location.assign(target);
    }
  }

  // Forget the session locally; never navigates to Keycloak.
  async localSignOut() {
    const realm = this.activeRealm();
    const m = realm ? this.manager(realm) : null;
    this._setRealm(null);
    this._setUser(null);
    if (m) await m.removeUser().catch(() => {});
  }
}

export default new OidcService();
